import { FishingReport } from '@omniafishing/core';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useFrontload } from 'react-frontload';
import { Route, RouteComponentProps, useRouteMatch } from 'react-router';
import Loading from '../components/loading/loading';
import { ScrollToTop } from '../components/scroll_to_top/scroll_to_top';
import { WaterbodyDetailContainer } from '../components/waterbody_detail/waterbody_detail_container';
import { sortReportsByMostRelevant } from '../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_fishing_reports/sortReportsByMostRelevant';
import { WaterbodyDetailCurrentConditionsSEO } from '../components/waterbody_detail/waterbody_sections_seo/waterbody_current_conditions_seo';
import { WaterbodyFishingReportsSEO } from '../components/waterbody_detail/waterbody_sections_seo/waterbody_fishing_reports_seo';
import { isProd } from '../env';
import { useResponsive } from '../hooks/use_responsive';
import { useRudderstack } from '../hooks/use_rudderstack';
import { apiV1 } from '../lib/api';
import { sortReportsByOutingDateOrAcceptedAt } from '../lib/fishing_reports';
import { formatToIsoDateZ } from '../lib/time';
import { waitFor } from '../lib/wait_for';
import { WaterbodyPaths } from '../routes';
import MapPageContainer from './map_page/map_page_container';
import { WaterbodyCurrentConditionsPage } from './waterbody_detail/waterbody_current_conditions_page';
import { WaterbodyFishingPatternsPageContainer } from './waterbody_detail/waterbody_fishing_patterns_page_container';
import { WaterbodyFishingReportsPage } from './waterbody_detail/waterbody_fishing_reports_page';

interface WaterbodyDetailRouterProps extends RouteComponentProps<{ url_slug: string }> {
  headerHeight: number;
}

export const WaterbodyDetailRouter = (props: WaterbodyDetailRouterProps) => {
  const { headerHeight } = props;
  const urlSlug = props.match.params.url_slug;
  const { path } = useRouteMatch();
  const { isMobile } = useResponsive();
  const { anonymousId } = useRudderstack();

  const { data, frontloadMeta } = useFrontload(`WaterbodyDetailRouter:${urlSlug}`, async () => {
    try {
      const [waterbodyRes, fishingReportsRes, sortedReportsRes] = await Promise.allSettled([
        apiV1.waterbodyFetch(urlSlug),
        apiV1.waterbodyFishingReportsFetch(urlSlug),
        /* will want more metadata for this request to remove big FR request
      mostRecentFishingReportDate, species in reports & their counts, see notion doc for more info
      */
        apiV1.waterbodyFishingReportsFetch(urlSlug, {
          sort: 'most_relevant',
          per_page: 9,
        }),
      ]);

      return {
        fishingReports:
          fishingReportsRes.status === 'fulfilled' ? fishingReportsRes.value.data.data : null,
        sortedFishingReports:
          sortedReportsRes.status === 'fulfilled' ? sortedReportsRes.value.data.data : null,
        totalFishingReportsCount:
          sortedReportsRes.status === 'fulfilled'
            ? sortedReportsRes.value.data.metadata.count_total
            : 0,
        waterbodyDetail: waterbodyRes.status === 'fulfilled' ? waterbodyRes.value.data.data : null,
      };
    } catch (e) {
      console.log(e);
    }
  });

  const [fishingReportsNearby, setFishingReportsNearby] = useState<FishingReport[]>([]);

  const fishingReports = data?.fishingReports || [];
  const sortedFishingReports = data?.sortedFishingReports || [];
  const waterbodyDetail = data?.waterbodyDetail;
  const totalFishingReportsCount = data?.totalFishingReportsCount;

  const fetchNearbyFishingReports = async () => {
    try {
      const res = await apiV1.forYouFishingReportsFetch({
        species: [],
        waterbodies: [waterbodyDetail.id],
        lat: waterbodyDetail.lat,
        lng: waterbodyDetail.lng,
        last_request_date: dayjs().subtract(90, 'days').format('YYYY-MM-DD'),
        anonymous_id: anonymousId,
        limit: 30,
      });
      const nearbyReports = res.data.data;
      setFishingReportsNearby(sortReportsByMostRelevant(nearbyReports));
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!totalFishingReportsCount && frontloadMeta.done) {
      fetchNearbyFishingReports();
    }
  }, [totalFishingReportsCount, frontloadMeta.done]);

  const mostRecentFishingReportDate = useMemo(() => {
    const reportsSortedByMostRecent = sortReportsByOutingDateOrAcceptedAt(fishingReports);
    const mostRecentAcceptedFishingReport = reportsSortedByMostRecent.find(
      (report) => report.accepted
    );
    return mostRecentAcceptedFishingReport
      ? dayjs(mostRecentAcceptedFishingReport?.accepted_at).startOf('day')
      : null;
  }, [fishingReports]);
  const lastUpdatedISODate = formatToIsoDateZ(mostRecentFishingReportDate);

  useEffect(() => {
    if (isProd() && waterbodyDetail) {
      waitFor(() => typeof (window as any).AF !== 'undefined', 10_000).then(() => {
        (window as any).AF('banners', 'updateParams', {
          waterbody_slug: waterbodyDetail.url_slug,
        });
      });
    }
  }, [waterbodyDetail?.url_slug]);

  if (frontloadMeta.pending) {
    return (
      <Loading text="Loading Waterbody Data" style={{ minHeight: isMobile ? '80dvh' : '40dvh' }} />
    );
  }

  if (!frontloadMeta.done || !waterbodyDetail) {
    return null;
  }

  return (
    <>
      {/* shared header will eventually go here */}
      <Route path={path} exact>
        <ScrollToTop>
          <WaterbodyDetailContainer
            fishingReports={fishingReports}
            fishingReportsNearby={fishingReportsNearby}
            mostRecentFishingReportDate={mostRecentFishingReportDate}
            sortedFishingReports={sortedFishingReports}
            totalFishingReportsCount={totalFishingReportsCount}
            waterbodyDetail={waterbodyDetail}
          />
        </ScrollToTop>
      </Route>
      <Route path={`${path}${WaterbodyPaths.CURRENT_CONDITIONS}`} exact>
        <MapPageContainer
          headerHeight={headerHeight}
          modalContent={<WaterbodyCurrentConditionsPage waterbodyDetail={waterbodyDetail} />}
          modalWaterbody={waterbodyDetail}
          seo={<WaterbodyDetailCurrentConditionsSEO waterbodyDetail={waterbodyDetail} />}
        />
      </Route>
      <Route path={`${path}${WaterbodyPaths.FISHING_REPORTS}`}>
        <MapPageContainer
          headerHeight={headerHeight}
          modalContent={
            <WaterbodyFishingReportsPage
              fishingReportsNearby={fishingReportsNearby}
              mostRecentFishingReportDate={mostRecentFishingReportDate}
              initialFishingReports={sortedFishingReports}
              totalFishingReportsCount={totalFishingReportsCount}
              waterbodyDetail={waterbodyDetail}
            />
          }
          modalWaterbody={waterbodyDetail}
          seo={
            <WaterbodyFishingReportsSEO
              waterbodyDetail={waterbodyDetail}
              lastUpdatedISODate={lastUpdatedISODate}
            />
          }
        />
      </Route>
      <Route path={`${path}${WaterbodyPaths.FISHING_PATTERNS}`}>
        <WaterbodyFishingPatternsPageContainer
          waterbodyDetail={waterbodyDetail}
          lastUpdatedISODate={lastUpdatedISODate}
        />
      </Route>
    </>
  );
};
export default WaterbodyDetailRouter;
